import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatApiErrorDetail(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Missing reset token. Please use the link from your email.');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, new_password: newPassword });
      toast.success('Password reset! Please sign in with your new password.');
      navigate('/login');
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg border border-slate-200 p-8">
          <div className="text-center mb-8">
            <img
              src="https://customer-assets-jt897jd0.emergentagent.net/job_family-athletics/artifacts/uy0gl72p_New%20Offcial%20Logo.jpg"
              alt="Dolphins FC Logo"
              className="h-24 mx-auto mb-4"
            />
            <h1 className="font-barlow-condensed text-3xl font-bold text-primary tracking-tight" data-testid="reset-password-title">
              Set a new password
            </h1>
            <p className="text-slate-600 mt-2 text-sm">Pick a strong password you don&apos;t use anywhere else.</p>
          </div>

          {!token ? (
            <div className="space-y-4" data-testid="reset-password-missing-token">
              <div className="rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-800">
                This link is missing a reset token. Please request a new password reset from the login page.
              </div>
              <Link to="/forgot-password" className="block text-center text-secondary hover:underline font-semibold">
                Request a new reset link
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
              <div>
                <Label htmlFor="new-password">New password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  autoFocus
                  data-testid="reset-password-new-input"
                  className="mt-1"
                />
                <p className="text-xs text-slate-500 mt-1">At least 6 characters.</p>
              </div>

              <div>
                <Label htmlFor="confirm-password">Confirm new password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="reset-password-confirm-input"
                  className="mt-1"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="reset-password-submit-button"
                className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-6 rounded-md transition-all duration-200"
              >
                {loading ? 'Updating password…' : 'Update password'}
              </Button>

              <div className="text-center">
                <Link to="/login" className="text-sm text-slate-600 hover:text-primary" data-testid="reset-back-to-login">
                  Back to sign in
                </Link>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
